/* eslint-disable @typescript-eslint/no-unused-vars */
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';

import { useIsDesktop } from '../../hooks/useIsDesktop';
import Button from '../Button';
import Clubs from '../Clubs';
import { IconPlayerIncidents } from '../IconPlayerIncidents';
import { DefaultTabsProps, Tabs } from '../Tabs';
import { TabType } from '../Types';

import { ItemAssistant } from './ItemsAssistant';
import { ReferencesFieldAssistance } from './ReferencesFieldAssistance';

import iconDetalle from '@/assets/ic_bars.svg';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import 'moment/dist/locale/es';
import { useIncidents } from '@/hooks/useIncidents';
import { useTournament } from '@/hooks/useTournament';
import ROUTES from '@/router/routes';
import { MyPlayersScore, PartidoUltimaFecha } from '@/types/fieldAssistance';
import { VariantFieldAssistance } from '@/types/team';
import { FADE_IN_OUT, cn } from '@/utils';

interface Props extends VariantFieldAssistance {
  matchs: PartidoUltimaFecha[];
}

export const RecentMatchStatsSection = ({ matchs }: Props) => {
  const { tab, handleTabChanges } = useHandleChangesTabs();
  const [state, setState] = useState(0);
  const { isDesktop, isMobile } = useIsDesktop();

  // lleno tabla de partidos
  const printFixture = useMemo(() => {
    return (
      <>
        <div className="flex justify-center mx-5 mt-10 text-lg mb-8">
          Partidos recientes
        </div>
        {matchs.length === 0 && (
          <div className="flex flex-col align-middle justify-center mx-5 mt-10 text-sm mb-8">
            <div className="w-auto text-center">
              Todavía no hay partidos cargados
            </div>
            <div className="w-auto text-center mt-8">
              <Button
                className={`font-bold text-sm justify-self-center ${
                  isMobile ? '' : 'mr-6'
                }`}
                href={ROUTES.summary}
                type="link"
                variant={isMobile ? 'secondary' : 'text'}
              >
                &nbsp;Volver a Inicio
              </Button>
            </div>
          </div>
        )}
        <div className="w-max-full lg:w-[950px] mx-auto">
          {matchs.map((match: PartidoUltimaFecha, index: number) => {
            const matchData = match.data;
            return (
              <div key={index}>
                <ItemAssistant.Fixture
                  golsLocal={match.ptosEquipoLocal}
                  golsVisitor={match.ptosEquipoVisitante}
                  idEstado={match.idEstado}
                  idLocal={matchData.idLocal}
                  idVisitor={matchData.idVisitante}
                  localClub={matchData.equipoLocal}
                  txtVerDetallePartido={
                    isMobile ? (
                      <img
                        alt="Ver Detalle"
                        className="w-4"
                        src={iconDetalle}
                      />
                    ) : (
                      'Ver Detalle del Partido'
                    )
                  }
                  variant={'SCORE_LAST_MATCH'}
                  visitorClub={matchData.equipoVisitante}
                  onClick={() => setState(index + 1)}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  }, [isMobile, matchs]);

  // lleno tablas de jugadores
  const printMatch = useMemo(() => {
    const indexMatch = state === 0 ? 0 : state - 1;
    if (state === 0) {
      return <></>;
    }

    const matchSelected =
      tab === TabType.FirstTab
        ? matchs[indexMatch].puntajeJugadoresEquipoLocal
        : matchs[indexMatch].puntajeJugadoresEquipoVisitante;

    // render items desktop equipo local
    return matchSelected.map((player: MyPlayersScore, index: number) => {
      return (
        <div key={index}>
          <PlayerWithEvents player={player} />
        </div>
      );
    });
  }, [matchs, state, tab]);

  const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: state > 0 ? matchs[state - 1].data.equipoLocal : '' },
      { name: state > 0 ? matchs[state - 1].data.equipoVisitante : '' },
    ];
  }, [matchs, state]);

  return (
    matchs.length >= 0 && (
      <motion.div
        key={state}
        animate={'show'}
        className="relative"
        initial={'hide'}
        variants={FADE_IN_OUT}
      >
        {/* {state > 0 && (        )} */}
        <div className={cn('mt-4 lg:mt-6 lg:rounded-lg lg:overflow-hidden')}>
          {state === 0 ? (
            printFixture
          ) : (
            <div className="w-max-full lg:w-[950px] mx-auto">
              <div
                className="text-sm lg:text-sm text-gdt-blue font-bold flex flex-1 flex-row ml-5 relative"
                onClick={() => {
                  setState(0);
                  handleTabChanges(TabType.FirstTab);
                }}
              >
                <span className="flex items-center cursor-pointer">
                  <IoIosArrowBack className="m-auto" size={16} />
                  &nbsp;Volver al Fixture
                </span>
              </div>
              <div className="px-4 mt-4 lg:mt-0  lg:ml-3 w-full flex items-center lg:justify-between flex-col mb-8">
                <Tabs
                  activeTab={tab}
                  className="!h-[48px] mt-2 lg:mt-0"
                  disabled={false}
                  tabs={tabs}
                  onClick={(tab: TabType) => handleTabChanges(tab)}
                />
              </div>
              <div className="lg:pl-9 h-14 flex items-center lg:p-0 px-4 mt-4 lg:mt-0 lg:bg-white lg:justify-end lg:border-b-[1px] lg:border-b-gdt-gray-7">
                <p className="text-sm lg:text-xs text-gdt-gray-2 font-bold flex-1">
                  Lista de Jugadores
                </p>
                <div className="flex max-w-[100px] lg:max-w-[344px] w-full text-2xs lg:text-sm text-gdt-gray-4 font-semibold text-center">
                  <p className="flex-1 text-right leading-tight lg:text-center w-[100px] lg:w-full lg:max-w-[344px]">
                    Puntos
                  </p>
                </div>
              </div>
              {<div className="bg-white">{printMatch}</div>}
              {isDesktop && <ReferencesFieldAssistance />}
            </div>
          )}
        </div>
      </motion.div>
    )
  );
};

interface PropsPlayerWithEvents {
  player: MyPlayersScore;
}
// ex ItemIncidents
const PlayerWithEvents = ({ player }: PropsPlayerWithEvents) => {
  const { isMobile, isDesktop } = useIsDesktop();

  const { tournamentMap } = useTournament();
  const teamsMap = tournamentMap?.tournamentTeamsMap;
  const club = teamsMap?.get(player?.clubId || -1);
  const { incidentsDesktop, incidentsMobile } = useIncidents(player);

  const randomNumber = Math.floor(Math.random() * 1000);

  return (
    <div className="px-4 lg:px-[0] lg:pl-7 flex lg:h-[63px] h-[83px] items-center border-b-[1px] border-b-gdt-gray-7 text-xs lg:text-sm">
      <div className="flex-1 flex gap-3">
        <figure className="w-[28px] h-[31px] relative top-1">
          <Clubs clubId={club?.id} playerId={player.id} type={'SHIRT'} />
        </figure>
        <div className="space-y-[5px] lg:space-x-4 lg:flex lg:items-center">
          <p className="text-xs lg:text-base font-bold text-gdt-gray flex">
            {player.nombre}
          </p>
          <p className="text-3xs lg:text-sm font-semibold text-gdt-gray-4 uppercase">
            {player.posicionCancha} - {player.club}
          </p>

          {isMobile && incidentsMobile && (
            <div className="w-full text-3xs lg:text-sm font-semibold text-gdt-blue uppercase lg:hidden flex">
              {incidentsMobile.join(', ')}
            </div>
          )}

          {isDesktop && incidentsDesktop && (
            <div className="items-center gap-2 hidden lg:flex">
              {incidentsDesktop.map((data, index) => {
                return (
                  <IconPlayerIncidents
                    key={index + randomNumber}
                    id={index + randomNumber}
                    text={data.text}
                    textTooltip={data.textTooltip && data.textTooltip}
                    variant={data.variant}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div
        className={cn(
          'text-right flex items-center w-[100px] lg:w-full font-bold text-sm text-gdt-gray lg:max-w-[344px]'
        )}
      >
        <p className="flex-1 lg:text-center">
          {player.isJugo ? player.ptosClarin : '-'}
        </p>
      </div>
    </div>
  );
};
